/**
 * Routes protégées par Cap.js (`@RequireCaptcha` côté backend) sur lesquelles le front obtient le
 * token AVANT le premier envoi : sans ça, la première tentative partait sans token, prenait un 403
 * CAPTCHA_REQUIRED, et le second envoi comptait un 2e hit sur la limite (5/min) — le « double clic »
 * de l'inscription (spec onboarding v4, écran 7). Une route protégée absente d'ici marche quand même
 * (403 puis nouvel essai avec token) ; une route listée qui n'exige plus de captcha coûterait une
 * preuve de travail pour rien : `captcha-routes.guard.spec.ts` (backend) vérifie que chaque entrée
 * porte bien `@RequireCaptcha`.
 *
 * Format : `MÉTHODE chemin`, segments dynamiques en `:nom`, sans slash initial.
 */
export const CAPTCHA_PRESOLVE_ROUTES: ReadonlyArray<string> = [
  'POST public/professional/login',
  'POST public/professional/signup',
  'POST public/professional/accept-invitation',
  'GET public/professional/:slug/contact-details',
  'POST public/professional/:slug/reveal-bindings',
  'POST public/professional/:slug/contact-submission',
  'POST public/booking/:slug/validate-zone-address',
  'POST public/booking/:uuid/cancel',
  'POST public/booking/:uuid/reschedule',
  'POST public/assistant/widget/message',
  'POST public/assistant/widget/presign',
];

const CAPTCHA_PRESOLVE_MATCHERS: ReadonlyArray<{ method: string; pattern: RegExp }> = CAPTCHA_PRESOLVE_ROUTES.map(
  (route): { method: string; pattern: RegExp } => {
    const [method, path] = route.split(' ');
    const source = path
      .split('/')
      .map((segment): string => (segment.startsWith(':') ? '[^/]+' : segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
      .join('/');
    return { method, pattern: new RegExp(`^${source}$`) };
  },
);

/** La requête vise-t-elle une route listée dans `CAPTCHA_PRESOLVE_ROUTES` ? (`url` sans query). */
export const isCaptchaPresolveRoute = (method: string, url: string): boolean => {
  const path = url.split('?')[0].replace(/^\/+/, '').replace(/\/+$/, '');
  const upper = method.toUpperCase();
  return CAPTCHA_PRESOLVE_MATCHERS.some((matcher): boolean => matcher.method === upper && matcher.pattern.test(path));
};
